import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';

import Flex from '@Components/Bases/Flex';
import OtherExplanation from '@Containers/OtherExplanations';
import {
  IImpactsOnStakeholdersQuery,
  useGetImpactsOnStakeholders,
  useGetImpactsOnStakeholdersOneReport,
} from '@Domains/ImpactsOnStakeholder';

import ConfirmModal from '@Components/Modal/ConfirmModal';
import { useDisclosure } from '@hooks/useDisclosure';
import SyncIcon from '@mui/icons-material/Sync';
import { convertBuddhistYearToGregorian } from '@Utils/datetime';
import { useCallback, useEffect, useState } from 'react';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import BusinessValueChains from './Containers/BusinessValueChain';
import ImpactsOnStakeholderFooter from './Containers/ImpactsOnStakeholderFooter';
import ImpactsOnStakeholderOverview from './Containers/ImpactsOnStakeholderOverview';
import ImpactsOnStakeholdersCheckAll from './Containers/ImpactsOnStakeholdersCheckAll';
import ImpactsOnStakeholdersFormProvider from './Containers/ImpactsOnStakeholdersFormProvider';
import StakeholderAnalysisValueChainOfBusiness from './Containers/StakeholderAnalysisValueChainOfBusiness/StakeholderAnalysisValueChainOfBusiness';
import { mapImpactsOnStakeholdersToFormValues } from './utils';

const ImpactsOnStakeholdersManagement = () => {
  const { id = '' } = useParams();
  const { user } = useProfile();
  const { progressData, isPublished } = useProgressBar();
  const location = useLocation();
  const currentPath = location.pathname.split('/').pop();
  const companyId = user?.I_COMPANY;
  const startOperatingYear = convertBuddhistYearToGregorian(
    progressData?.report.startOperatingYear
  );
  const OPERATING_YEAR_OFFSET = 1;
  const previousStartOperatingYear = startOperatingYear - OPERATING_YEAR_OFFSET;

  const [
    openedConfirmModal,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure();
  const { refetchProgressWithUpdate } = useProgressBar();
  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();
  const [{ data, loading, error }, refetch] = useGetImpactsOnStakeholders(id);
  const [
    { loading: loadingOneReport, error: errorOneReport },
    getImpactsOnStakeholdersOneReport,
  ] = useGetImpactsOnStakeholdersOneReport(
    id,
    Number(companyId),
    previousStartOperatingYear
  );

  const [isCheckSyncOneReport, setIsCheckSyncOneReport] = useState(false);
  const [
    dataImpactOnStakeholdersOneReport,
    setDataImpactOnStakeholdersOneReport,
  ] = useState<IImpactsOnStakeholdersQuery | undefined>(undefined);

  useEffect(() => {
    if (currentPath === 'impacts-on-stakeholders-management') {
      refetch().catch((err) => {
        if (err.name !== 'CanceledError') {
          console.error(
            'Failed to fetch impacts on stakeholders management :',
            err
          );
        }
      });
    }
  }, [currentPath, refetch]);

  const fetchOneReportData = useCallback(async () => {
    try {
      const response = await getImpactsOnStakeholdersOneReport();

      if (response.data) {
        setDataImpactOnStakeholdersOneReport(response.data);
        setIsCheckSyncOneReport(true);
      }
    } catch (error) {
      console.error('Error fetching One Report data:', error);
      setDataImpactOnStakeholdersOneReport(undefined);
    } finally {
      openSnackbar();
      closeConfirmModal();
      refetchProgressWithUpdate(true);
      await refetch();
    }
  }, [getImpactsOnStakeholdersOneReport, refetch, closeConfirmModal]);

  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity={'error'}>
          ไม่สามารถโหลดข้อมูลการจัดการผลกระทบต่อผู้มีส่วนได้เสียในห่วงโซ่คุณค่าของธุรกิจได้
        </Alert>
      </Box>
    );
  }

  const initialImpactOnStakeholderFormValues =
    mapImpactsOnStakeholdersToFormValues(
      isCheckSyncOneReport ? dataImpactOnStakeholdersOneReport : data
    );

  return (
    <>
      <ImpactsOnStakeholdersFormProvider
        initialValues={initialImpactOnStakeholderFormValues}
      >
        <Flex flexDirection={'column'} gap={2} p={2} mb={8}>
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="h3">ส่วนที่ 1 การประกอบธุรกิจ</Typography>
            {!isPublished && (
              <Button
                color={'inherit'}
                startIcon={<SyncIcon />}
                onClick={openConfirmModal}
              >
                ดึงข้อมูลจาก One Report
              </Button>
            )}
          </Flex>
          <ImpactsOnStakeholderOverview />
          <BusinessValueChains />
          <StakeholderAnalysisValueChainOfBusiness />
          <OtherExplanation />
          <ImpactsOnStakeholdersCheckAll />
        </Flex>
        <ImpactsOnStakeholderFooter />
      </ImpactsOnStakeholdersFormProvider>

      <ConfirmModal
        opened={openedConfirmModal}
        close={closeConfirmModal}
        loading={loadingOneReport}
        disableBackdropClose
        topic="ดึงข้อมูลล่าสุด"
        description={`เมื่อกดยืนยันระบบจะดึงข้อมูลทุกรายการจากระบบ One Report \n
            และจะทำการแทนที่ข้อมูลที่ได้กรอกไว้ก่อนหน้านี้`}
        confirm={fetchOneReportData}
      />

      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!errorOneReport ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {errorOneReport?.status === 404
            ? 'ไม่พบข้อมูลจาก One Report'
            : !!errorOneReport
            ? 'เกิดข้อผิดพลาดในการดึงข้อมูล'
            : 'ดึงข้อมูลสำเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImpactsOnStakeholdersManagement;
